import React, { useMemo, useState } from 'react';
import { Button, Card, Form } from 'react-bootstrap';
import { useMapEvents } from 'react-leaflet';

const menuStyles = {
  position: 'absolute',
  zIndex: 700,
  minWidth: 200,
};

export default function ToolsContextMenu({
  showZoneTools,
  showTourTools,
  showZoneEditor,
  showAllZones = false,
  suppressOnTargets = [],
  suppressContextMenuRef = null,
  allowTourTools = true,
  allowZoneEditorToggle = true,
  setShowZoneTools,
  setShowTourTools,
  setShowZoneEditor,
  setShowAllZones,
  setMapTool,
}) {
  const [menuState, setMenuState] = useState(null);

  const map = useMapEvents({
    contextmenu: (e) => {
      const now = Date.now();
      if (
        suppressContextMenuRef &&
        typeof suppressContextMenuRef === 'object' &&
        Number(suppressContextMenuRef.current) > now
      ) {
        setMenuState(null);
        return;
      }
      const target = e?.originalEvent?.target;
      const shouldSuppress =
        target?.closest &&
        Array.isArray(suppressOnTargets) &&
        suppressOnTargets.some((selector) => selector && target.closest(selector));
      if (shouldSuppress) {
        setMenuState(null);
        return;
      }
      e.originalEvent?.preventDefault?.();
      const pt = map.latLngToContainerPoint(e.latlng);
      setMenuState({
        latlng: e.latlng,
        point: pt,
      });
    },
    click: (e) => {
      const target = e?.originalEvent?.target;
      if (target?.closest && target.closest('.tools-context-menu')) {
        return;
      }
      setMenuState(null);
    },
    keydown: (e) => {
      if (e.originalEvent?.key === 'Escape') {
        setMenuState(null);
      }
    },
  });

  const positionStyle = useMemo(() => {
    if (!menuState) return { display: 'none' };
    return { left: menuState.point.x, top: menuState.point.y };
  }, [menuState]);

  if (!menuState) return null;

  const zoneEditorOn = allowZoneEditorToggle ? !!showZoneEditor : true;
  const showAllTools = allowTourTools
    ? showZoneTools && showTourTools && zoneEditorOn
    : showZoneTools && zoneEditorOn;

  const handleAddZone = (shape) => {
    // Start placement mode so the next left-click drops the zone where the user clicks.
    setMapTool?.({ mode: 'zone', shape });
    setShowZoneTools?.(true);
    setMenuState(null);
  };

  const handleSetCheckpointTool = (geometry) => {
    setMapTool?.({ mode: 'checkpoint', geometry });
    setShowTourTools?.(true);
    setMenuState(null);
  };

  const handleSetStartTool = () => {
    setMapTool?.({ mode: 'start' });
    setShowTourTools?.(true);
    setMenuState(null);
  };

  return (
    <Card
      className="tools-context-menu responder-map-context-menu border border-theme-strong shadow-lg bg-primary-shadow text-theme"
      style={{ ...menuStyles, ...positionStyle }}
      onMouseDown={(e) => {
        e.stopPropagation();
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <Card.Body className="p-2 d-flex flex-column gap-2">
        {allowTourTools && (
          <>
            <div className="fw-semibold small text-uppercase text-theme-muted">tour</div>
            <div className="d-flex flex-column gap-1">
              <Button
                size="sm"
                variant="outline-secondary"
                className="responder-map-context-button responder-interactive-action"
                onClick={() => handleSetCheckpointTool('circle')}
              >
                Place circle checkpoint
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                className="responder-map-context-button responder-interactive-action"
                onClick={() => handleSetCheckpointTool('polygon')}
              >
                Place polygon checkpoint
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                className="responder-map-context-button responder-interactive-action"
                onClick={() => handleSetCheckpointTool('crossline')}
              >
                Place crossline checkpoint
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                className="responder-map-context-button responder-interactive-action"
                onClick={handleSetStartTool}
              >
                Set start location
              </Button>
            </div>
            <hr className="my-2" />
          </>
        )}
        <div className="fw-semibold small text-uppercase text-theme-muted">Zones</div>
        <div className="d-flex flex-column gap-1">
          <Button
            size="sm"
            variant="outline-secondary"
            className="responder-map-context-button responder-interactive-action"
            onClick={() => handleAddZone('square')}
          >
            Add zone (square)
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            className="responder-map-context-button responder-interactive-action"
            onClick={() => handleAddZone('circle')}
          >
            Add zone (circle)
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            className="responder-map-context-button responder-interactive-action"
            onClick={() => handleAddZone('triangle')}
          >
            Add zone (triangle)
          </Button>
        </div>
        <div className="d-flex justify-content-between align-items-center mt-1">
          <span className="small text-theme-muted">Always Show Tools</span>
          <Button
            size="sm"
            variant={showAllTools ? 'primary' : 'outline-secondary'}
            className={`responder-map-context-toggle responder-interactive-action ${showAllTools ? 'is-selected' : ''}`}
            onClick={() => {
              const next = !showAllTools;
              setShowZoneTools?.(next);
              if (allowTourTools) {
                setShowTourTools?.(next);
              }
              if (allowZoneEditorToggle) {
                setShowZoneEditor?.(next);
              }
              setMenuState(null);
            }}
          >
            {showAllTools ? 'On' : 'Off'}
          </Button>
        </div>
        {typeof setShowAllZones === 'function' && (
          <div className="d-flex justify-content-between align-items-center mt-1">
            <span className="small text-theme-muted">Show all Zones</span>
            <Form.Check
              type="switch"
              id="toggle-show-all-zones-context"
              checked={!!showAllZones}
              onChange={(e) => {
                const next = e.target.checked;
                setShowAllZones(next);
                setMenuState(null);
              }}
              label=""
            />
          </div>
        )}
        {allowTourTools && (
          <div className="d-flex justify-content-between align-items-center mt-1">
            <span className="small text-theme-muted">Toggle Tour Tools</span>
            <Form.Check
              type="switch"
              id="toggle-tools"
              checked={!!showTourTools}
              onChange={(e) => {
                const next = e.target.checked;
                setShowTourTools?.(next);
                setMenuState(null);
              }}
              label=""
            />
          </div>
        )}
        <div className="d-flex justify-content-between align-items-center mt-1">
          <span className="small text-theme-muted">Toggle Zone Tools</span>
          <Form.Check
            type="switch"
            id="toggle-zone-tools"
            checked={!!showZoneTools}
            onChange={(e) => {
              const next = e.target.checked;
              setShowZoneTools?.(next);
              setMenuState(null);
            }}
            label=""
          />
        </div>
        {allowZoneEditorToggle && (
          <div className="d-flex justify-content-between align-items-center mt-1">
            <span className="small text-theme-muted">Toggle Zone Editor</span>
            <Form.Check
              type="switch"
              id="toggle-zone-editor"
              checked={!!showZoneEditor}
              onChange={(e) => {
                const next = e.target.checked;
                setShowZoneEditor?.(next);
                setMenuState(null);
              }}
              label=""
            />
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
